export const CHECKOUT_URL = 'https://technician-en.impultienda.ar/checkout/'

export const COUNTDOWN_SECONDS = 17 * 60 + 34

export const customerSees = [
  'How you answer their message',
  'How you present your price',
  'Whether you send a professional estimate',
  'How organized your service feels',
  'How you explain the warranty',
  'How you follow up',
  'How easy it is to do business with you',
]

export const beforeItems = [
  'You improvise every quote',
  'Customers ask for your price before you know your costs',
  'Your WhatsApp conversations are scattered',
  'You don’t always have a professional service order',
  'Warranty conditions aren’t clearly documented',
  'You forget to follow up with customers',
  'You lose opportunities for reviews and referrals',
  'Your business depends too much on memory',
  'Every customer experience feels different',
]

export const afterItems = [
  'Use professional quote templates',
  'Present your prices with confidence',
  'Organize every customer interaction',
  'Use professional service documents',
  'Set clear warranty expectations',
  'Follow a simple customer follow-up system',
  'Ask for reviews and referrals systematically',
  'Create a consistent customer experience',
  'Make your business look established from day one',
]

export const recognizeItems = [
  {
    emoji: '😓',
    text: 'You know how to repair appliances, but your business still feels improvised.',
  },
  {
    emoji: '💬',
    text: 'You spend too much time typing the same answers to customers on WhatsApp.',
  },
  {
    emoji: '💰',
    text: 'You sometimes feel uncomfortable explaining why your repair costs what it costs.',
  },
  {
    emoji: '📄',
    text: 'You don’t have professional documents for every stage of the service.',
  },
  {
    emoji: '🛡️',
    text: 'You have dealt with customers who misunderstood what your warranty actually covered.',
  },
  {
    emoji: '⭐',
    text: 'You know satisfied customers could refer you, but you don’t have a system for asking.',
  },
  {
    emoji: '⏱️',
    text: 'You waste valuable time creating documents, messages, and quotes from scratch.',
  },
  {
    emoji: '😩',
    text: 'You want customers to see you as a serious professional — not simply “the guy who fixes appliances.”',
  },
]

export type Volume = {
  label: string
  title: string
  hook: string
  body: string
  items: string[]
}

export const volumes: Volume[] = [
  {
    label: 'VOLUME I',
    title: 'THE PROFESSIONAL TECHNICIAN SETUP',
    hook: 'Build the professional foundation your customers see from the very first interaction.',
    body: 'Create the basic structure your appliance repair business needs to operate consistently — without spending weeks figuring everything out yourself.',
    items: [
      'Professional business setup checklist',
      'Service information templates',
      'Customer information forms',
      'Service policies',
      'Business information sheet',
      'Service area worksheet',
      'Operating hours template',
      'Professionalization checklist',
      'First-contact standards',
      'Customer communication guidelines',
    ],
  },
  {
    label: 'VOLUME II',
    title: 'QUOTE WITH CONFIDENCE™',
    hook: 'Stop improvising your prices and start presenting your estimates like a professional service company.',
    body: 'Learn how to structure and present your repair estimates so customers understand what they’re paying for and why your service has value.',
    items: [
      'Professional estimate templates',
      'Diagnostic fee structure',
      'Labor pricing framework',
      'Parts and materials structure',
      'Travel/service-call pricing',
      'Minimum service pricing',
      'Additional service charges',
      'Discount guidelines',
      'Estimate presentation scripts',
      'Price objection responses',
      'Quote follow-up scripts',
    ],
  },
  {
    label: 'VOLUME III',
    title: 'THE ZERO-CHAOS SERVICE SYSTEM™',
    hook: 'Create a consistent process from the moment a customer contacts you until the repair is completed.',
    body: 'Instead of reinventing your process for every job, follow a simple professional workflow.',
    items: [
      'Customer intake process',
      'Service request form',
      'Appointment confirmation',
      'Technician arrival checklist',
      'Diagnostic workflow',
      'Approval process',
      'Repair checklist',
      'Parts tracking',
      'Job status system',
      'Quality-control checklist',
      'Completion checklist',
      'Payment confirmation',
      'Service closing process',
    ],
  },
  {
    label: 'VOLUME IV',
    title: 'PREMIUM CUSTOMER EXPERIENCE™',
    hook: 'Make every customer interaction feel organized, professional, and worth paying more for.',
    body: 'Your customers may not know how good you are technically. But they can immediately recognize a professional experience.',
    items: [
      'First-contact scripts',
      'Appointment confirmation messages',
      'Arrival messages',
      'Diagnosis explanations',
      'Estimate presentation scripts',
      'Repair completion messages',
      'Customer education scripts',
      'Thank-you messages',
      'Review request scripts',
      'Referral request scripts',
      'Follow-up messages',
      'Customer experience checklist',
    ],
  },
  {
    label: 'VOLUME V',
    title: 'THE CUSTOMER FOLLOW-UP SYSTEM™',
    hook: 'Turn completed repairs into reviews, repeat business, and referrals.',
    body: 'Most technicians stop communicating when the appliance is fixed. This system gives you a simple follow-up process to stay in front of customers without becoming pushy.',
    items: [
      '24-hour follow-up',
      '7-day follow-up',
      '30-day follow-up',
      'Review request sequence',
      'Referral request sequence',
      'Past-customer reactivation',
      'Seasonal follow-up ideas',
      'Repeat-service opportunities',
      'Customer database checklist',
      'Follow-up calendar',
      'Ready-to-send messages',
    ],
  },
  {
    label: 'VOLUME VI',
    title: 'THE PREMIUM SERVICE DOCUMENT VAULT™',
    hook: 'The professional documents you need to stop building everything from scratch.',
    body: 'This is the practical heart of the Premium Technician Kit™. You’ll receive ready-to-customize templates for:',
    items: [
      'Customer intake forms',
      'Service orders',
      'Estimates',
      'Invoices',
      'Payment receipts',
      'Warranty documents',
      'Repair completion forms',
      'Customer records',
      'Parts records',
      'Service checklists',
      'Quality-control forms',
      'Follow-up forms',
      'Review requests',
      'Referral cards',
      'Customer communication templates',
      'Service policies',
      'Business checklists',
    ],
  },
]

export const extras = [
  {
    emoji: '📝',
    title: 'READY-TO-USE CUSTOMER SCRIPTS',
    text: 'Professional messages for the situations appliance repair technicians deal with every day.',
  },
  {
    emoji: '📋',
    title: 'PROFESSIONAL CHECKLISTS',
    text: 'Simple checklists to help you create consistency and avoid forgetting important steps.',
  },
  {
    emoji: '📄',
    title: 'EDITABLE BUSINESS TEMPLATES',
    text: 'Customize your documents instead of creating them from zero.',
  },
  {
    emoji: '💬',
    title: 'WHATSAPP COMMUNICATION SCRIPTS',
    text: 'Messages for inquiries, appointments, estimates, follow-ups, payments, reviews, and referrals.',
  },
  {
    emoji: '⭐',
    title: 'CUSTOMER EXPERIENCE TOOLS',
    text: 'Everything you need to make the customer experience feel more organized from beginning to end.',
  },
]

export const journeySteps = [
  'Message',
  'Intake',
  'Appointment',
  'Diagnosis',
  'Estimate',
  'Approval',
  'Repair',
  'Payment',
  'Warranty',
  'Follow-Up',
  'Review',
  'Referral',
]

export type Bonus = {
  tag: string
  emoji: string
  title: string
  value: string
  hook: string
  intro?: string
  items: string[]
  note?: string
  outro?: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    tag: 'BONUS #1',
    emoji: '📲',
    title: 'WHATSAPP TECHNICIAN SCRIPT LIBRARY™',
    value: '$47',
    hook: 'Stop wasting time wondering what to say to customers.',
    intro: 'Get ready-to-customize scripts for:',
    items: [
      'New inquiries',
      'Price questions',
      'Appointment scheduling',
      'Delayed appointments',
      'Diagnosis explanations',
      'Estimate delivery',
      'Follow-up',
      'Payment reminders',
      'Warranty questions',
      'Review requests',
      'Referral requests',
      'Difficult customers',
    ],
    outro: 'Copy. Customize. Send.',
    image:
      'https://cdn.magicpatterns.com/uploads/ixeGxLyzBbReSkbj4BtzPE/BONUS_1_TECHNICIAN-EN.png',
  },
  {
    tag: 'BONUS #2',
    emoji: '💰',
    title: 'PRICE WITH CONFIDENCE™',
    value: '$47',
    hook: 'A practical pricing toolkit designed to help you understand your service costs and present your prices with greater confidence.',
    intro: 'Includes:',
    items: [
      'Cost calculator framework',
      'Labor pricing worksheet',
      'Service-call calculator',
      'Parts markup worksheet',
      'Minimum-price calculator',
      'Profitability checklist',
      'Price objection scripts',
      'Discount decision guide',
    ],
    image:
      'https://cdn.magicpatterns.com/uploads/pA5ManZ8W2Se95gpdZ6cC7/BONUS_2_TECHNICIAN-EN.png',
  },
  {
    tag: 'BONUS #3',
    emoji: '🛡️',
    title: 'WARRANTY PROTECTION KIT™',
    value: '$47',
    hook: 'Clear communication prevents unnecessary conflicts.',
    intro: 'Get professional templates and scripts to clearly communicate:',
    items: [
      'What your warranty covers',
      'What it doesn’t cover',
      'Warranty conditions',
      'Customer responsibilities',
      'Warranty claim process',
      'Warranty communication scripts',
      'Service completion confirmation',
      'Warranty documentation checklist',
    ],
    note: 'Important: Customize warranty language to comply with the laws and requirements applicable to your location.',
    image:
      'https://cdn.magicpatterns.com/uploads/nvTRQ4QyiyT515Knss85zb/BONUS_3_TECHNICIAN-EN.png',
  },
  {
    tag: 'BONUS #4',
    emoji: '⭐',
    title: '5-STAR REVIEW & REFERRAL SYSTEM™',
    value: '$47',
    hook: 'Turn happy customers into your best marketing channel.',
    intro: 'Includes:',
    items: [
      'Review request scripts',
      'Timing guide',
      'Referral request scripts',
      'Follow-up messages',
      'Thank-you messages',
      'Customer appreciation templates',
      'Referral tracking sheet',
      '30-day referral activation plan',
    ],
    image:
      'https://cdn.magicpatterns.com/uploads/qr8Ldf97mY37YK4bmX3jcG/BONUS_4_TECHNICIAN-EN.png',
  },
  {
    tag: 'BONUS #5',
    emoji: '📅',
    title: '30-DAY PROFESSIONALIZATION CHALLENGE™',
    value: '$47',
    hook: 'Instead of trying to change everything at once, follow one simple action every day for 30 days.',
    intro: 'You’ll progressively improve:',
    items: [
      'Your customer communication',
      'Your quotes',
      'Your service documents',
      'Your warranty process',
      'Your follow-up',
      'Your customer experience',
      'Your business organization',
      'Your professional presentation',
    ],
    outro: 'One day. One improvement. One step closer to a professional operation.',
    image:
      'https://cdn.magicpatterns.com/uploads/9Zx6Ro64bqTcmS25TK1q8K/BONUS_5_TECHNICIAN-EN.png',
  },
]

export type Testimonial = {
  quote: string
  name: string
  location: string
  photo: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'I finally stopped sending customers random messages and started using a consistent process. The templates alone saved me hours.',
    name: '[Verified Customer Name]',
    location: '[City, Country]',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/6d8e566d-4303-4811-aed1-6813f84266c0.jpg',
  },
  {
    quote:
      'The quote templates changed how I present my prices. Customers now understand exactly what they’re paying for.',
    name: '[Verified Customer Name]',
    location: '[City, Country]',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/73f16583-1ad4-4d1c-a4db-bfb9533d7455.jpg',
  },
  {
    quote:
      'I didn’t need another course. I needed professional documents I could actually use. That’s exactly what this gave me.',
    name: '[Verified Customer Name]',
    location: '[City, Country]',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/8fb62859-1aec-42b0-8204-d4d76a4b2ec8.jpg',
  },
  {
    quote:
      'The WhatsApp scripts are incredibly useful. I don’t have to figure out what to say every time a customer asks the same question.',
    name: '[Verified Customer Name]',
    location: '[City, Country]',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/12bfe8a3-3544-4228-a9fa-bd1ac9995bb9.jpg',
  },
  {
    quote:
      'The biggest difference is how professional the entire customer experience feels now.',
    name: '[Verified Customer Name]',
    location: '[City, Country]',
    photo:
      'https://cdn.magicpatterns.com/patterns/generated-images/1e1f0ed8-0aa8-411a-a5c1-5a9db5191b90.jpg',
  },
]

export const creatorGaps = [
  'presenting estimates',
  'organizing customer information',
  'communicating professionally',
  'documenting services',
  'managing warranties',
  'following up',
  'generating reviews',
  'creating referrals',
  'building a consistent customer experience',
]

export const faqs = [
  {
    q: 'How will I receive the materials?',
    a: 'Immediately after your purchase, you’ll receive instructions to access and download the Premium Technician Kit™ and its bonus materials.',
  },
  {
    q: 'Is this only for appliance repair technicians?',
    a: 'The system is designed specifically around appliance repair businesses. Many of the principles may also be useful for other service professionals, but the examples, scripts, and templates are primarily designed for appliance repair technicians.',
  },
  {
    q: 'Does it work in my country?',
    a: 'The core business tools are designed to be adaptable to different markets. However, you should customize pricing, taxes, invoices, payment methods, warranty language, and legal documents according to the laws and requirements applicable in your location.',
  },
  {
    q: 'Do I need business or marketing experience?',
    a: 'No. The system is designed for technicians who want practical tools they can implement without needing a background in business administration or marketing.',
  },
  {
    q: 'Are the templates editable?',
    a: 'Yes. The materials are designed to be customized with your business name, logo, contact details, prices, policies, and other relevant information.',
  },
  {
    q: 'Is this a monthly subscription?',
    a: 'No. It’s a one-time payment of $27 USD with lifetime access to the purchased materials.',
  },
  {
    q: 'Is this a course?',
    a: 'No. The Premium Technician Kit™ is a practical toolkit containing guides, systems, scripts, checklists, workflows, and ready-to-customize templates.',
  },
  {
    q: 'What if I don’t like it?',
    a: 'You have a 7-day money-back guarantee. Explore the system and determine whether it provides the value you expected. If it doesn’t, contact us within the applicable guarantee period.',
  },
]

export const finalTools = [
  'QUOTING',
  'CUSTOMER COMMUNICATION',
  'SERVICE DOCUMENTATION',
  'WARRANTIES',
  'FOLLOW-UP',
  'REVIEWS & REFERRALS',
]

export const purchaseNotifications = [
  { name: 'Carlos M.', country: 'Mexico' },
  { name: 'Javier R.', country: 'Spain' },
  { name: 'Luis F.', country: 'Colombia' },
  { name: 'Miguel A.', country: 'Argentina' },
  { name: 'Ricardo P.', country: 'Peru' },
  { name: 'Andrés G.', country: 'Chile' },
  { name: 'José L.', country: 'Dominican Republic' },
  { name: 'Marcela V.', country: 'Ecuador' },
  { name: 'Fernando T.', country: 'Uruguay' },
  { name: 'Diego S.', country: 'Guatemala' },
  { name: 'Rubén C.', country: 'Costa Rica' },
  { name: 'Alejandro N.', country: 'Panama' },
  { name: 'Daniela H.', country: 'Bolivia' },
  { name: 'Óscar B.', country: 'Paraguay' },
  { name: 'Iván Q.', country: 'Venezuela' },
  { name: 'Pablo E.', country: 'Honduras' },
]
